import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData, { formatPrice, siteConfig } from "../Datas/productData";
import "../css/main.scss";

function Main() {
  const { addToCart, toggleWishlist, wishlistItems } = useContext(CartContext);
  const [addedProductId, setAddedProductId] = useState(null);

  const featuredProducts = useMemo(
    () => productData.filter((product) => product.featured).slice(0, 8),
    []
  );

  useEffect(() => {
    if (!addedProductId) return undefined;
    const timer = window.setTimeout(() => setAddedProductId(null), 1600);
    return () => window.clearTimeout(timer);
  }, [addedProductId]);

  const handleQuickAdd = (product) => {
    addToCart(product.id, 1, product.sizes[0], product.colors[0]);
    setAddedProductId(product.id);
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const storyProduct = (category) =>
    productData.find((product) => product.category === category) || productData[0];

  const categoryStories = [
    {
      title: "Carry your day",
      eyebrow: "Bags",
      image: storyProduct("Bags").images[1],
      query: "Bags",
    },
    {
      title: "Easy, everyday wear",
      eyebrow: "Wear",
      image: storyProduct("Wear").images[1],
      query: "Wear",
    },
    {
      title: "Thoughtful little things",
      eyebrow: "Gifting",
      image: storyProduct("Desk & Stationery").images[1],
      query: "Desk & Stationery",
    },
  ];

  return (
    <div className="main home-page">
      <main>
        <section className="home-hero" aria-labelledby="home-hero-title">
          <img
            className="home-hero__image"
            src="/img/hero-everyday.jpg"
            alt="A woman carrying an olive bag through a sunlit courtyard"
            width="2400"
            height="1350"
          />
          <div className="home-hero__shade" />
          <div className="home-hero__content">
            <p className="eyebrow">The everyday collection</p>
            <h1 id="home-hero-title">Made for all the ways you move.</h1>
            <p>
              Soft structure, useful details, and colours that settle easily into
              every day.
            </p>
            <Link className="button button--light" to="/shop" onClick={scrollToTop}>
              Shop the collection
            </Link>
          </div>
        </section>

        <section className="home-marquee" aria-label="Store highlights">
          <div className="home-marquee__track">
            {[0, 1].map((group) => (
              <div className="home-marquee__group" key={group} aria-hidden={group === 1}>
                <span>Worldwide delivery</span>
                <span>Designed for real life</span>
                <span>
                  Flat-rate tracked shipping {formatPrice(siteConfig.shipping.standardRate)}
                </span>
                <span>Small rituals, thoughtfully made</span>
              </div>
            ))}
          </div>
        </section>

        <section className="home-products page-shell" aria-labelledby="new-and-noted">
          <div className="section-heading section-heading--split">
            <div>
              <p className="eyebrow">New & noted</p>
              <h2 id="new-and-noted">Everyday favourites</h2>
            </div>
            <Link to="/shop" onClick={scrollToTop} className="text-link">
              Shop all <span aria-hidden="true">↗</span>
            </Link>
          </div>

          <div className="home-products__grid">
            {featuredProducts.map((product, index) => {
              const isWishlisted = wishlistItems.includes(product.id);
              return (
                <article className="product-card" key={product.id}>
                  <div className="product-card__media">
                    <Link
                      to={`/shopDetails/${product.id}`}
                      onClick={scrollToTop}
                      aria-label={`View ${product.name}`}
                    >
                      <img
                        className="product-card__image product-card__image--primary"
                        src={product.images[0]}
                        alt={product.name}
                        width="1120"
                        height="1400"
                        loading={index < 4 ? "eager" : "lazy"}
                      />
                      <img
                        className="product-card__image product-card__image--secondary"
                        src={product.images[1]}
                        alt=""
                        width="1120"
                        height="1400"
                        loading="lazy"
                      />
                    </Link>
                    {product.badge && (
                      <span className="product-card__badge">{product.badge}</span>
                    )}
                    <button
                      type="button"
                      className={`product-card__wish${isWishlisted ? " is-active" : ""}`}
                      onClick={() => toggleWishlist(product.id)}
                      aria-pressed={isWishlisted}
                      aria-label={`${isWishlisted ? "Remove" : "Add"} ${product.name} ${
                        isWishlisted ? "from" : "to"
                      } wishlist`}
                    >
                      <span aria-hidden="true">{isWishlisted ? "♥" : "♡"}</span>
                    </button>
                    <button
                      type="button"
                      className="product-card__quick-add"
                      onClick={() => handleQuickAdd(product)}
                    >
                      {addedProductId === product.id ? "Added" : "Quick add"}
                    </button>
                  </div>
                  <div className="product-card__details">
                    <Link to={`/shopDetails/${product.id}`} onClick={scrollToTop}>
                      <h3>{product.name}</h3>
                    </Link>
                    <p>{formatPrice(product.price)}</p>
                  </div>
                  <div className="product-card__swatches" aria-label="Available colours">
                    {product.colors.slice(0, 3).map((color) => (
                      <span key={color}>{color}</span>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="home-manifesto" aria-label="Our design philosophy">
          <div className="home-manifesto__inner page-shell">
            <p className="eyebrow">Considered by design</p>
            <h2>
              We make useful things feel a little more beautiful—and beautiful
              things work a little harder.
            </h2>
            <Link className="text-link" to="/about" onClick={scrollToTop}>
              Our story <span aria-hidden="true">↗</span>
            </Link>
          </div>
        </section>

        <section className="home-stories" aria-labelledby="shop-by-rhythm">
          <div className="page-shell">
            <div className="section-heading">
              <p className="eyebrow">Find your rhythm</p>
              <h2 id="shop-by-rhythm">Designed around real days</h2>
            </div>
          </div>
          <div className="home-stories__grid">
            {categoryStories.map((story) => (
              <Link
                className="home-story"
                key={story.title}
                to={`/shop?category=${encodeURIComponent(story.query)}`}
                onClick={scrollToTop}
              >
                <img
                  src={story.image}
                  alt=""
                  width="1120"
                  height="1400"
                  loading="lazy"
                />
                <span className="home-story__shade" />
                <span className="home-story__content">
                  <span className="eyebrow">{story.eyebrow}</span>
                  <strong>{story.title}</strong>
                  <span>Explore <span aria-hidden="true">↗</span></span>
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="home-promise page-shell" aria-labelledby="home-promise-title">
          <div className="home-promise__index">01</div>
          <div>
            <p className="eyebrow">Objects with intention</p>
            <h2 id="home-promise-title">Fewer, better, longer-loved things.</h2>
          </div>
          <p>
            {siteConfig.brandName} pieces begin with an everyday need, then are
            refined through material choices, useful details, and a lighter
            footprint wherever possible.
          </p>
        </section>
      </main>
    </div>
  );
}

export default Main;
